import React, { useState } from 'react';
import { usePage, router, Link } from '@inertiajs/react';
import FileUpload from '@/components/FileUpload';
import FolderList from '@/components/FolderList';
import FileList from '@/components/FileList';
import CreateFolderDialog from '@/components/CreateFolderDialog';
import Breadcrumbs from '@/components/Breadcrumbs';
import BulkActions from '@/components/BulkActions';
import { useMultiSelect } from '@/lib/hooks/useMultiSelect';
import { Button } from '@/components/ui/button';
import { FolderPlus, Upload, HardDrive } from 'lucide-react';

interface Folder {
    id: number;
    name: string;
    parent_id: number | null;
    created_at: string;
    files_count: number;
    subfolders_count: number;
}

interface File {
    id: number;
    name: string;
    description: string | null;
    size: string;
    size_bytes: number;
    type: string;
    extension: string;
    is_image: boolean;
    is_pdf: boolean;
    created_at: string;
    url: string | null;
    download_url: string;
}

interface Breadcrumb {
    name: string;
    path: string;
}

interface PageProps {
    folders: Folder[];
    files: File[];
    current_folder: { id: number; name: string; parent_id: number | null; path: string } | null;
    breadcrumbs: Breadcrumb[];
    storage_used: number;
    files_count: number;
    flash?: {
        success?: string;
        error?: string;
    };
}

export default function Index() {
    const { folders, files, current_folder, breadcrumbs, storage_used, files_count, flash } =
        usePage<PageProps>().props;

    const [showUpload, setShowUpload] = useState(false);
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const { selectedIds, toggleSelect, clearSelection, isSelected } = useMultiSelect();

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleBulkMove = (targetFolderId: number | null) => {
        router.post('/uploaded_files/bulk_move', {
            file_ids: selectedIds,
            folder_id: targetFolderId
        }, {
            onSuccess: () => clearSelection()
        });
    };

    const handleBulkDelete = () => {
        if (confirm(`Are you sure you want to delete ${selectedIds.length} file(s)?`)) {
            router.delete('/uploaded_files/bulk_delete', {
                data: { file_ids: selectedIds },
                onSuccess: () => clearSelection()
            });
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="border-b">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-3xl font-bold">My Files</h1>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                                <div className="flex items-center gap-2">
                                    <HardDrive className="h-4 w-4" />
                                    <span>{formatBytes(storage_used)} used</span>
                                </div>
                                <div>
                                    <span>{files_count} files</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" className='cursor-pointer'>
                                <Link href="/session" method="delete" as="div">
                                    Wyloguj
                                </Link>
                            </Button>

                            <Button className='cursor-pointer' onClick={() => setShowCreateFolder(true)} variant="outline">
                                <FolderPlus className="h-4 w-4 mr-2" />
                                New Folder
                            </Button>
                            <Button className='cursor-pointer' onClick={() => setShowUpload(true)}>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload File
                            </Button>
                        </div>
                    </div>

                    <Breadcrumbs items={breadcrumbs} />
                </div>
            </div>

            <div className="container mx-auto px-4 py-6">
                {flash?.success && (
                    <div className="mb-4 p-3 rounded-md bg-green-50 text-green-700 text-sm">
                        {flash.success}
                    </div>
                )}
                {flash?.error && (
                    <div className="mb-4 p-3 rounded-md bg-destructive/15 text-destructive text-sm">
                        {flash.error}
                    </div>
                )}

                {selectedIds.length > 0 && (
                    <BulkActions
                        selectedCount={selectedIds.length}
                        onClearSelection={clearSelection}
                        onDelete={handleBulkDelete}
                        folders={folders}
                        currentFolderId={current_folder?.id}
                        onMove={handleBulkMove}
                    />
                )}

                {folders.length === 0 && files.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-muted-foreground">
                            <p className="text-lg mb-4">This folder is empty</p>
                            <p className="text-sm">Upload files or create folders to get started</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {folders.length > 0 && (
                            <FolderList
                                folders={folders}
                                onDrop={handleBulkMove}
                                isDragging={selectedIds.length > 0}
                            />
                        )}
                        {files.length > 0 && (
                            <FileList
                                files={files}
                                selectedIds={selectedIds}
                                onToggleSelect={toggleSelect}
                                isSelected={isSelected}
                            />
                        )}
                    </div>
                )}
            </div>

            {showUpload && (
                <FileUpload
                    folderId={current_folder?.id}
                    onClose={() => setShowUpload(false)}
                />
            )}

            {showCreateFolder && (
                <CreateFolderDialog
                    parentId={current_folder?.id}
                    onClose={() => setShowCreateFolder(false)}
                />
            )}
        </div>
    );
}